import { db, auth } from '../firebase'
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  onSnapshot,
  updateDoc,
  doc, 
  deleteDoc,
  serverTimestamp,
  getDocs,
  writeBatch
} from 'firebase/firestore'
import { isDevUser, getDevUser, addDevDoc, updateDevDoc, deleteDevDoc, getDevDocs as getDevDocsService } from './devService'

/**
 * Crea una nueva notificación para un propietario
 * @param {string} ownerId - ID del propietario que recibirá la notificación
 * @param {string} type - Tipo de notificación (upload, interest, assigned, signed, payment)
 * @param {string} contractId - ID del contrato relacionado
 * @param {string} contractTitle - Título del contrato
 * @param {string} message - Mensaje descriptivo
 * @param {Object} additionalData - Datos adicionales específicos del tipo de notificación
 * @returns {Promise<string>} - ID de la notificación creada
 */
export async function createNotification(ownerId, type, contractId, contractTitle, message, additionalData = {}) {
  try {
    const notificationData = {
      userId: ownerId,
      type,
      contractId,
      contractTitle,
      message,
      timestamp: serverTimestamp(),
      read: false,
      data: additionalData
    }

    // Usar el servicio de desarrollo si es necesario
    if (isDevUser()) {
      const createdDoc = await addDevDoc('notifications', notificationData)
      return createdDoc.id
    }

    // Crear notificación en Firestore
    const docRef = await addDoc(collection(db, 'notifications'), notificationData)
    return docRef.id
  } catch (error) {
    console.error('Error al crear notificación:', error)
    throw error
  }
}

/**
 * Configura un listener para las notificaciones del usuario actual
 * @param {function} callback - Función que recibe las notificaciones actualizadas
 * @param {number} limitCount - Número máximo de notificaciones a recuperar
 * @returns {function} - Función para cancelar la suscripción
 */
export function subscribeToNotifications(callback, limitCount = 20) {
  // Obtener el ID del usuario actual
  const currentUser = isDevUser() ? getDevUser() : auth.currentUser
  
  if (!currentUser) {
    console.warn('No hay usuario autenticado para recibir notificaciones')
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {} // Función vacía para cancelar suscripción (nada que cancelar si no hay usuario)
  }

  // Usar el servicio de desarrollo si es necesario
  if (isDevUser()) {
    // Simulación de suscripción en modo desarrollo
    const fetchAndNotify = async () => {
      try {
        const notifications = await getDevDocsService('notifications', {
          userId: currentUser.uid,
          limit: limitCount,
          orderBy: { field: 'timestamp', direction: 'desc' }
        })
        callback(notifications)
      } catch (error) {
        console.error('Error al obtener notificaciones en modo dev:', error)
      }
    }

    // Ejecutar inicialmente y configurar intervalo de actualización
    fetchAndNotify()
    const intervalId = setInterval(fetchAndNotify, 5000)
    return () => clearInterval(intervalId)
  }

  // Configurar query para Firestore
  const notificationsQuery = query(
    collection(db, 'notifications'),
    where('userId', '==', currentUser.uid),
    orderBy('timestamp', 'desc'),
    limit(limitCount)
  )

  // Suscribirse a cambios en tiempo real
  return onSnapshot(notificationsQuery, (snapshot) => {
    const notifications = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
    callback(notifications)
  }, (error) => {
    console.error('Error en suscripción a notificaciones:', error)
  })
}

/**
 * Marca una notificación como leída
 * @param {string} notificationId - ID de la notificación
 * @returns {Promise<void>}
 */
export async function markNotificationAsRead(notificationId) {
  try {
    if (isDevUser()) {
      await updateDevDoc('notifications', notificationId, { read: true })
      return
    }

    const notificationRef = doc(db, 'notifications', notificationId)
    await updateDoc(notificationRef, { read: true })
  } catch (error) {
    console.error('Error al marcar notificación como leída:', error)
    throw error
  }
}

/**
 * Marca todas las notificaciones del usuario como leídas
 * @returns {Promise<void>}
 */
export async function markAllNotificationsAsRead() {
  try {
    const currentUser = isDevUser() ? getDevUser() : auth.currentUser
    
    if (!currentUser) {
      console.warn('No hay usuario autenticado para actualizar notificaciones')
      return
    }

    if (isDevUser()) {
      const notifications = await getDevDocsService('notifications', {
        userId: currentUser.uid,
        where: { field: 'read', operator: '==', value: false }
      })

      // Actualizar cada notificación
      for (const notification of notifications) {
        await updateDevDoc('notifications', notification.id, { read: true })
      }
      return
    }

    // En producción, esto debería hacerse con una función de Firebase para actualizaciones masivas
    // Esta implementación es simplificada y puede no ser eficiente para muchas notificaciones
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', currentUser.uid),
      where('read', '==', false)
    )

    const snapshot = await getDocs(notificationsQuery)
    
    // Actualizar cada documento
    const batch = writeBatch(db)
    snapshot.forEach(documentSnapshot => {
      batch.update(documentSnapshot.ref, { read: true })
    })
    
    await batch.commit()
  } catch (error) {
    console.error('Error al marcar todas las notificaciones como leídas:', error)
    throw error
  }
}

/**
 * Elimina una notificación
 * @param {string} notificationId - ID de la notificación a eliminar
 * @returns {Promise<void>}
 */
export async function deleteNotification(notificationId) {
  try {
    if (isDevUser()) {
      await deleteDevDoc('notifications', notificationId)
      return
    }

    const notificationRef = doc(db, 'notifications', notificationId)
    await deleteDoc(notificationRef)
  } catch (error) {
    console.error('Error al eliminar notificación:', error)
    throw error
  }
}

// Funciones específicas para los diferentes tipos de notificaciones

/**
 * Crea una notificación de contrato subido
 * @param {string} ownerId - ID del propietario
 * @param {string} contractId - ID del contrato
 * @param {string} contractTitle - Título del contrato
 * @returns {Promise<string>} - ID de la notificación creada
 */
export function notifyContractUploaded(ownerId, contractId, contractTitle) {
  return createNotification(
    ownerId,
    'upload',
    contractId,
    contractTitle,
    'Tu contrato ha sido creado correctamente'
  )
}

/**
 * Crea una notificación de interés en contrato
 * @param {string} ownerId - ID del propietario
 * @param {string} contractId - ID del contrato
 * @param {string} contractTitle - Título del contrato
 * @param {string} applicantId - ID del aplicante interesado
 * @param {string} applicantName - Nombre del aplicante interesado
 * @returns {Promise<string>} - ID de la notificación creada
 */
export function notifyInterestInContract(ownerId, contractId, contractTitle, applicantId, applicantName) {
  return createNotification(
    ownerId,
    'interest',
    contractId,
    contractTitle,
    `${applicantName} ha mostrado interés en tu contrato`,
    { applicantId, applicantName }
  )
}

/**
 * Crea una notificación de contrato asignado
 * @param {string} ownerId - ID del propietario
 * @param {string} contractId - ID del contrato
 * @param {string} contractTitle - Título del contrato
 * @param {string} applicantId - ID del aplicante asignado
 * @param {string} applicantName - Nombre del aplicante asignado
 * @returns {Promise<string>} - ID de la notificación creada
 */
export function notifyContractAssigned(ownerId, contractId, contractTitle, applicantId, applicantName) {
  return createNotification(
    ownerId,
    'assigned',
    contractId,
    contractTitle,
    `Has asignado el contrato a ${applicantName}`,
    { applicantId, applicantName }
  )
}

/**
 * Crea una notificación para el aplicante cuando es asignado a un contrato
 * @param {string} applicantId - ID del aplicante
 * @param {string} contractId - ID del contrato
 * @param {string} contractTitle - Título del contrato
 * @param {string} ownerName - Nombre del propietario
 * @returns {Promise<string>} - ID de la notificación creada
 */
export function notifyApplicantAssigned(applicantId, contractId, contractTitle, ownerName) {
  return createNotification(
    applicantId,
    'assigned_to_you',
    contractId,
    contractTitle,
    `Has sido seleccionado para el contrato por ${ownerName}`,
    { ownerName }
  )
}

/**
 * Crea una notificación de contrato firmado
 * @param {string} ownerId - ID del propietario
 * @param {string} contractId - ID del contrato
 * @param {string} contractTitle - Título del contrato
 * @param {string} applicantId - ID del aplicante que firmó
 * @param {string} applicantName - Nombre del aplicante que firmó
 * @returns {Promise<string>} - ID de la notificación creada
 */
export function notifyContractSigned(ownerId, contractId, contractTitle, applicantId, applicantName) {
  return createNotification(
    ownerId,
    'signed',
    contractId,
    contractTitle,
    `${applicantName} ha firmado el contrato`,
    { applicantId, applicantName }
  )
}

/**
 * Crea una notificación para el aplicante cuando el propietario firma
 * @param {string} applicantId - ID del aplicante
 * @param {string} contractId - ID del contrato
 * @param {string} contractTitle - Título del contrato
 * @param {string} ownerName - Nombre del propietario
 * @returns {Promise<string>} - ID de la notificación creada
 */
export function notifyOwnerSigned(applicantId, contractId, contractTitle, ownerName) {
  return createNotification(
    applicantId,
    'owner_signed',
    contractId,
    contractTitle,
    `${ownerName} ha firmado el contrato`,
    { ownerName }
  )
}

/**
 * Crea una notificación de pago recibido
 * @param {string} ownerId - ID del propietario
 * @param {string} contractId - ID del contrato
 * @param {string} contractTitle - Título del contrato
 * @param {string} applicantId - ID del aplicante que pagó
 * @param {string} applicantName - Nombre del aplicante que pagó
 * @param {number} amount - Cantidad pagada
 * @returns {Promise<string>} - ID de la notificación creada
 */
export function notifyPaymentReceived(ownerId, contractId, contractTitle, applicantId, applicantName, amount) {
  return createNotification(
    ownerId,
    'payment',
    contractId,
    contractTitle,
    `${applicantName} ha realizado el pago de la tarifa de compromiso`,
    { applicantId, applicantName, amount }
  )
}

/**
 * Crea una notificación para el aplicante cuando el propietario realiza el pago
 * @param {string} applicantId - ID del aplicante
 * @param {string} contractId - ID del contrato
 * @param {string} contractTitle - Título del contrato
 * @param {string} ownerName - Nombre del propietario
 * @param {number} amount - Cantidad pagada
 * @returns {Promise<string>} - ID de la notificación creada
 */
export function notifyOwnerPaymentReceived(applicantId, contractId, contractTitle, ownerName, amount) {
  return createNotification(
    applicantId,
    'owner_payment',
    contractId,
    contractTitle,
    `${ownerName} ha realizado el pago de la tarifa de compromiso`,
    { ownerName, amount }
  )
} 

// =======================================
// FUNCIONES DE NOTIFICACIÓN PARA COLIVINGS
// =======================================

/**
 * Crea una notificación cuando se envía una solicitud de coliving
 * @param {string} agentId - ID del agente que gestiona el coliving
 * @param {string} colivingId - ID del coliving
 * @param {string} colivingTitle - Título del coliving
 * @param {string} applicantName - Nombre del solicitante
 * @param {string} applicationId - ID de la solicitud
 * @returns {Promise<string>} - ID de la notificación creada
 */
export function notifyColivingApplicationSubmitted(agentId, colivingId, colivingTitle, applicantName, applicationId) {
  return createNotification(
    agentId,
    'coliving_application_submitted',
    colivingId,
    colivingTitle,
    `${applicantName} ha enviado una solicitud para ${colivingTitle}`,
    { applicantName, applicationId, colivingId }
  )
}

/**
 * Crea una notificación cuando una solicitud de coliving es pre-aprobada
 * @param {string} applicantId - ID del solicitante
 * @param {string} colivingId - ID del coliving
 * @param {string} colivingTitle - Título del coliving
 * @param {string} roomInfo - Información de la habitación asignada
 * @param {string} applicationId - ID de la solicitud
 * @returns {Promise<string>} - ID de la notificación creada
 */
export function notifyColivingPreApproved(applicantId, colivingId, colivingTitle, roomInfo, applicationId) {
  return createNotification(
    applicantId,
    'coliving_pre_approved',
    colivingId,
    colivingTitle,
    `Tu solicitud para ${colivingTitle} ha sido pre-aprobada. Habitación: ${roomInfo}`,
    { roomInfo, applicationId, colivingId }
  )
}

/**
 * Crea una notificación cuando el inquilino firma el contrato de coliving
 * @param {string} agentId - ID del agente
 * @param {string} colivingId - ID del coliving
 * @param {string} colivingTitle - Título del coliving
 * @param {string} applicantName - Nombre del inquilino
 * @param {string} applicationId - ID de la solicitud
 * @returns {Promise<string>} - ID de la notificación creada
 */
export function notifyColivingApplicantSigned(agentId, colivingId, colivingTitle, applicantName, applicationId) {
  return createNotification(
    agentId,
    'coliving_applicant_signed',
    colivingId,
    colivingTitle,
    `${applicantName} ha firmado el contrato para ${colivingTitle}`,
    { applicantName, applicationId, colivingId }
  )
}

/**
 * Crea una notificación cuando se completa el contrato de coliving (ambas partes han firmado)
 * @param {string} userId - ID del usuario (inquilino o agente)
 * @param {string} colivingId - ID del coliving
 * @param {string} colivingTitle - Título del coliving
 * @param {string} applicationId - ID de la solicitud
 * @param {boolean} isForApplicant - Si la notificación es para el inquilino
 * @returns {Promise<string>} - ID de la notificación creada
 */
export function notifyColivingContractCompleted(userId, colivingId, colivingTitle, applicationId, isForApplicant = false) {
  const message = isForApplicant 
    ? `¡Tu contrato para ${colivingTitle} está completamente firmado! Ahora puedes proceder con el pago.`
    : `El contrato para ${colivingTitle} está completamente firmado y listo para el proceso de pago.`
    
  return createNotification(
    userId,
    'coliving_contract_completed',
    colivingId,
    colivingTitle,
    message,
    { applicationId, colivingId, isForApplicant }
  )
}

/**
 * Crea una notificación cuando se completa el pago del coliving
 * @param {string} agentId - ID del agente
 * @param {string} colivingId - ID del coliving
 * @param {string} colivingTitle - Título del coliving
 * @param {string} applicantName - Nombre del inquilino
 * @param {string} applicationId - ID de la solicitud
 * @returns {Promise<string>} - ID de la notificación creada
 */
export function notifyColivingPaymentCompleted(agentId, colivingId, colivingTitle, applicantName, applicationId) {
  return createNotification(
    agentId,
    'coliving_payment_completed',
    colivingId,
    colivingTitle,
    `${applicantName} ha completado el pago para ${colivingTitle}. Puedes proceder con la asignación final.`,
    { applicantName, applicationId, colivingId }
  )
}

/**
 * Crea una notificación cuando se confirma la asignación final de habitación
 * @param {string} applicantId - ID del inquilino
 * @param {string} colivingId - ID del coliving
 * @param {string} colivingTitle - Título del coliving
 * @param {string} roomInfo - Información de la habitación
 * @param {string} applicationId - ID de la solicitud
 * @returns {Promise<string>} - ID de la notificación creada
 */
export function notifyColivingAssignmentConfirmed(applicantId, colivingId, colivingTitle, roomInfo, applicationId) {
  return createNotification(
    applicantId,
    'coliving_assignment_confirmed',
    colivingId,
    colivingTitle,
    `¡Tu asignación de habitación en ${colivingTitle} ha sido confirmada! Habitación: ${roomInfo}`,
    { roomInfo, applicationId, colivingId }
  )
} 